/**
 * @fileoverview Componente responsável por orquestrar a visualização completa de um ticket.
 */

import React, { useState, useEffect, useContext, useRef, lazy, Suspense } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import clsx from "clsx";

import { Paper, makeStyles, CircularProgress } from "@material-ui/core";

import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

// ✅ Code Splitting: Lazy load de componentes pesados
const ContactDrawer = lazy(() => import("../ContactDrawer"));
const MessageInput = lazy(() => import("../MessageInputCustom/"));
const MessagesList = lazy(() => import("../MessagesList"));
const MessageSearch = lazy(() => import("../MessageSearch"));

const drawerWidth = 360;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    containIntrinsicSize: "100% 100%",
    contain: "layout style",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    position: "sticky",
    top: 0,
    alignItems: "stretch",
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);

  const socketManager = useContext(SocketContext);

  // Ref para manter o ticketId atual acessível dentro do handler do socket
  // Resolve o problema de closure stale quando ticket é removido das dependências
  const ticketIdRef = useRef(ticketId);

  useEffect(() => {
    setLoading(true);
    const fetchTicket = async () => {
      try {
        const { data } = await api.get("/tickets/" + ticketId);
        const { queueId } = data;
        const { queues, profile } = user;

        // Ticket sem fila (queueId null) é liberado para todos os perfis,
        // espelhando o comportamento da lista (TicketsManagerTabs).
        // Apenas bloqueia quando o ticket TEM fila e essa fila não pertence ao usuário.
        const isNoQueue = queueId === null || queueId === undefined;
        const queueAllowed = queues.find((q) => q.id === queueId);
        if (!isNoQueue && queueAllowed === undefined && profile !== "admin") {
          toast.error(i18n.t("tickets.toasts.unauthorized"));
          history.push("/tickets");
          return;
        }

        setContact(data.contact);
        setTicket(data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };
    fetchTicket();
  }, [ticketId, user, history]);

  // Mantém o ref atualizado quando ticketId muda
  useEffect(() => {
    ticketIdRef.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleReady = () => {
      socket.emit("joinChatBox", `${ticketIdRef.current}`);
    };

    // Também emite joinChatBox imediatamente caso o socket já esteja conectado
    if (socket.connected) {
      socket.emit("joinChatBox", `${ticketIdRef.current}`);
    }

    const handleTicketUpdate = (data) => {
      // Usa ticketIdRef.current para sempre ter acesso ao valor atual
      const currentTicketId = parseInt(ticketIdRef.current);

      if (data.action === "update" && data.ticket.id === currentTicketId) {
        setTicket(data.ticket);
      }

      if (data.action === "delete" && data.ticketId === currentTicketId) {
        // toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    };

    const handleContactUpdate = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    const handleWhatsAppSession = (data) => {
      if (data.action === "update" && data.session) {
        // Atualiza o whatsapp no ticket se for o mesmo whatsappId
        setTicket((prevTicket) => {
          if (prevTicket.whatsappId === data.session.id) {
            return {
              ...prevTicket,
              whatsapp: {
                ...prevTicket.whatsapp,
                status: data.session.status,
                name: data.session.name
              }
            };
          }
          return prevTicket;
        });
      }
    };

    socket.on("ready", handleReady);
    socket.on(`company-${companyId}-ticket`, handleTicketUpdate);
    socket.on(`company-${companyId}-contact`, handleContactUpdate);
    socket.on(`company-${companyId}-whatsappSession`, handleWhatsAppSession);

    return () => {
      socket.off("ready", handleReady);
      socket.off(`company-${companyId}-ticket`, handleTicketUpdate);
      socket.off(`company-${companyId}-contact`, handleContactUpdate);
      socket.off(`company-${companyId}-whatsappSession`, handleWhatsAppSession);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, history, socketManager]); // ✅ Removido 'ticket' das dependências para evitar re-renders

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleOpenSearch = () => {
    setSearchOpen(true);
  };

  const handleCloseSearch = () => {
    setSearchOpen(false);
  };

  const handleMessageSelect = (message) => {
    // Implementa a navegação para a mensagem específica
    // A mensagem será destacada e o scroll será feito até ela
    const messageElement = document.getElementById(`message-${message.id}`);
    // ✅ Verificar se elemento existe E se ainda está no DOM
    if (messageElement && document.contains(messageElement)) {
      try {
        // Scroll suave para a mensagem
        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        // Adiciona um destaque temporário à mensagem
        setTimeout(() => {
          // ✅ Verificar novamente antes de adicionar classe
          if (messageElement && document.contains(messageElement)) {
            messageElement.classList.add('message-highlight');
            setTimeout(() => {
              // ✅ Verificar novamente antes de remover classe
              if (messageElement && document.contains(messageElement)) {
                messageElement.classList.remove('message-highlight');
              }
            }, 2000);
          }
        }, 100);
      } catch (error) {
        // ✅ Silenciar erros de DOM (elemento pode ter sido removido)
        console.debug("handleMessageSelect scrollIntoView failed (element removed):", error);
      }
    }
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined || ticket.aiAgent) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
          onTicketUpdate={setTicket}
        />
      );
    }
  };

  const renderMessagesList = () => {
    return (
      <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}><CircularProgress /></div>}>
        <MessagesList
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
        ></MessagesList>
        <MessageInput ticketId={ticket.id} ticketStatus={ticket.status} ticket={ticket} />
      </Suspense>
    );
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          {renderTicketInfo()}
          <TicketActionButtons ticket={ticket} onOpenSearch={handleOpenSearch} onTicketUpdate={setTicket} />
        </TicketHeader>
        {/* Tags movidas para o menu da setinha na lista de tickets */}
        {/* <Paper>
          <TagsContainer ticket={ticket} />
        </Paper> */}
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      </Paper>
      <Suspense fallback={null}>
        <ContactDrawer
          open={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          contact={contact}
          loading={loading}
          ticket={ticket}
        />
      </Suspense>

      <Suspense fallback={null}>
        <MessageSearch
          open={searchOpen}
          onClose={handleCloseSearch}
          ticketId={ticket.id}
          contactName={contact.name}
          onMessageSelect={handleMessageSelect}
        />
      </Suspense>
    </div>
  );
};

export default Ticket;
